/**
 * QrCode — a tiny, dependency-free QR code generator that renders an SVG.
 *
 * Why inline instead of a library (hotfix-1-159):
 *   The landing page only needs to encode one short URL (the site origin).
 *   Pulling a full QR npm package (qrcode / qrcode.react) would add a build
 *   dependency for a single static use. This self-contained encoder supports
 *   byte mode with error-correction level M and auto-selects the smallest QR
 *   version that fits the payload — more than enough for a ~40-char URL.
 *
 * Output is an <svg> so it stays crisp at any size and respects currentColor.
 * Accessibility: pass a descriptive `label`; it becomes role="img" + aria-label.
 *
 * The encoder below is a compact, standalone implementation of the QR spec
 * (ISO/IEC 18004) for byte mode. It is intentionally minimal — no kanji/numeric
 * modes, no version > 10 — which keeps it small while covering all URL payloads
 * we render here.
 */

// ---------------------------------------------------------------------------
// Galois field (GF(256)) tables for Reed–Solomon error correction.
// ---------------------------------------------------------------------------
const GF_EXP = new Uint8Array(512);
const GF_LOG = new Uint8Array(256);
(() => {
  let x = 1;
  for (let i = 0; i < 255; i++) {
    GF_EXP[i] = x;
    GF_LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d; // primitive polynomial
  }
  for (let i = 255; i < 512; i++) GF_EXP[i] = GF_EXP[i - 255];
})();

function gfMul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return GF_EXP[GF_LOG[a] + GF_LOG[b]];
}

function rsGeneratorPoly(degree: number): number[] {
  let poly = [1];
  for (let i = 0; i < degree; i++) {
    const next = new Array(poly.length + 1).fill(0);
    for (let j = 0; j < poly.length; j++) {
      next[j] ^= poly[j];
      next[j + 1] ^= gfMul(poly[j], GF_EXP[i]);
    }
    poly = next;
  }
  return poly;
}

function rsEncode(data: number[], ecLen: number): number[] {
  const gen = rsGeneratorPoly(ecLen);
  const res = new Array(ecLen).fill(0);
  for (const byte of data) {
    const factor = byte ^ res[0];
    res.shift();
    res.push(0);
    for (let i = 0; i < gen.length - 1; i++) {
      res[i] ^= gfMul(gen[i + 1], factor);
    }
  }
  return res;
}

// ---------------------------------------------------------------------------
// Per-version capacity (byte mode, EC level M) and EC block layout.
// version → { totalDataCodewords, ecPerBlock, [blocksGroup1, blocksGroup2] }
// Covers versions 1–10, which hold up to ~287 bytes — ample for any URL.
// ---------------------------------------------------------------------------
interface VersionSpec {
  version: number;
  dataCodewords: number; // total data codewords across all blocks
  ecPerBlock: number;
  group1Blocks: number;
  group1DataPerBlock: number;
  group2Blocks: number;
  group2DataPerBlock: number;
}

// Level M layout (ISO/IEC 18004 Table 9).
const VERSIONS_M: VersionSpec[] = [
  { version: 1, dataCodewords: 16, ecPerBlock: 10, group1Blocks: 1, group1DataPerBlock: 16, group2Blocks: 0, group2DataPerBlock: 0 },
  { version: 2, dataCodewords: 28, ecPerBlock: 16, group1Blocks: 1, group1DataPerBlock: 28, group2Blocks: 0, group2DataPerBlock: 0 },
  { version: 3, dataCodewords: 44, ecPerBlock: 26, group1Blocks: 1, group1DataPerBlock: 44, group2Blocks: 0, group2DataPerBlock: 0 },
  { version: 4, dataCodewords: 64, ecPerBlock: 18, group1Blocks: 2, group1DataPerBlock: 32, group2Blocks: 0, group2DataPerBlock: 0 },
  { version: 5, dataCodewords: 86, ecPerBlock: 24, group1Blocks: 2, group1DataPerBlock: 43, group2Blocks: 0, group2DataPerBlock: 0 },
  { version: 6, dataCodewords: 108, ecPerBlock: 16, group1Blocks: 4, group1DataPerBlock: 27, group2Blocks: 0, group2DataPerBlock: 0 },
  { version: 7, dataCodewords: 124, ecPerBlock: 18, group1Blocks: 4, group1DataPerBlock: 31, group2Blocks: 0, group2DataPerBlock: 0 },
  { version: 8, dataCodewords: 154, ecPerBlock: 22, group1Blocks: 2, group1DataPerBlock: 38, group2Blocks: 2, group2DataPerBlock: 39 },
  { version: 9, dataCodewords: 182, ecPerBlock: 22, group1Blocks: 3, group1DataPerBlock: 36, group2Blocks: 2, group2DataPerBlock: 37 },
  { version: 10, dataCodewords: 216, ecPerBlock: 26, group1Blocks: 4, group1DataPerBlock: 43, group2Blocks: 1, group2DataPerBlock: 44 },
];

// Error-correction bits for the format string at EC level M (0b00).
const FORMAT_EC_BITS_M = 0b00;

// Alignment-pattern centre coordinates per version (1–10).
const ALIGN_POS: Record<number, number[]> = {
  1: [],
  2: [6, 18],
  3: [6, 22],
  4: [6, 26],
  5: [6, 30],
  6: [6, 34],
  7: [6, 22, 38],
  8: [6, 24, 42],
  9: [6, 26, 46],
  10: [6, 28, 50],
};

function encodeByteMode(text: string): { spec: VersionSpec; bytes: number[] } {
  // UTF-8 encode the payload.
  const utf8 = Array.from(new TextEncoder().encode(text));

  const spec = VERSIONS_M.find((v) => {
    // 4 mode bits + char-count bits (8 for v1–9 byte mode, 16 for v10) + data.
    const countBits = v.version >= 10 ? 16 : 8;
    const totalBits = 4 + countBits + utf8.length * 8;
    return totalBits <= v.dataCodewords * 8;
  });
  if (!spec) {
    throw new Error('QR payload too long for supported versions (max 10).');
  }

  const countBits = spec.version >= 10 ? 16 : 8;

  // Build the bit stream.
  const bits: number[] = [];
  const pushBits = (value: number, len: number) => {
    for (let i = len - 1; i >= 0; i--) bits.push((value >> i) & 1);
  };

  pushBits(0b0100, 4); // byte mode indicator
  pushBits(utf8.length, countBits);
  for (const b of utf8) pushBits(b, 8);

  // Terminator (up to 4 zero bits) and byte alignment.
  const capacityBits = spec.dataCodewords * 8;
  const terminator = Math.min(4, capacityBits - bits.length);
  for (let i = 0; i < terminator; i++) bits.push(0);
  while (bits.length % 8 !== 0) bits.push(0);

  // Pad with the spec's alternating pad bytes.
  const padBytes = [0xec, 0x11];
  let padIndex = 0;
  while (bits.length < capacityBits) {
    pushBits(padBytes[padIndex % 2], 8);
    padIndex++;
  }

  // Bits → data codewords.
  const dataCodewords: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    let byte = 0;
    for (let j = 0; j < 8; j++) byte = (byte << 1) | bits[i + j];
    dataCodewords.push(byte);
  }

  // Split into blocks, compute EC per block, then interleave.
  const blocks: { data: number[]; ec: number[] }[] = [];
  let pos = 0;
  for (let i = 0; i < spec.group1Blocks; i++) {
    const data = dataCodewords.slice(pos, pos + spec.group1DataPerBlock);
    pos += spec.group1DataPerBlock;
    blocks.push({ data, ec: rsEncode(data, spec.ecPerBlock) });
  }
  for (let i = 0; i < spec.group2Blocks; i++) {
    const data = dataCodewords.slice(pos, pos + spec.group2DataPerBlock);
    pos += spec.group2DataPerBlock;
    blocks.push({ data, ec: rsEncode(data, spec.ecPerBlock) });
  }

  const maxData = Math.max(...blocks.map((b) => b.data.length));
  const finalBytes: number[] = [];
  for (let i = 0; i < maxData; i++) {
    for (const b of blocks) if (i < b.data.length) finalBytes.push(b.data[i]);
  }
  for (let i = 0; i < spec.ecPerBlock; i++) {
    for (const b of blocks) finalBytes.push(b.ec[i]);
  }

  return { spec, bytes: finalBytes };
}

// ---------------------------------------------------------------------------
// Matrix construction.
// ---------------------------------------------------------------------------
type Matrix = (boolean | null)[][];

function buildMatrix(spec: VersionSpec, bytes: number[]): boolean[][] {
  const size = spec.version * 4 + 17;
  const matrix: Matrix = Array.from({ length: size }, () => new Array<boolean | null>(size).fill(null));
  const reserved: boolean[][] = Array.from({ length: size }, () => new Array<boolean>(size).fill(false));

  const setFn = (r: number, c: number, val: boolean, isFunction: boolean) => {
    matrix[r][c] = val;
    if (isFunction) reserved[r][c] = true;
  };

  // Finder pattern + separator at a corner.
  const placeFinder = (rowOff: number, colOff: number) => {
    for (let r = -1; r <= 7; r++) {
      for (let c = -1; c <= 7; c++) {
        const rr = rowOff + r;
        const cc = colOff + c;
        if (rr < 0 || rr >= size || cc < 0 || cc >= size) continue;
        const isBorder = r === 0 || r === 6 || c === 0 || c === 6;
        const isCore = r >= 2 && r <= 4 && c >= 2 && c <= 4;
        const inFinder = r >= 0 && r <= 6 && c >= 0 && c <= 6;
        setFn(rr, cc, inFinder && (isBorder || isCore), true);
      }
    }
  };
  placeFinder(0, 0);
  placeFinder(0, size - 7);
  placeFinder(size - 7, 0);

  // Timing patterns.
  for (let i = 8; i < size - 8; i++) {
    setFn(6, i, i % 2 === 0, true);
    setFn(i, 6, i % 2 === 0, true);
  }

  // Alignment patterns.
  const centres = ALIGN_POS[spec.version];
  for (const r of centres) {
    for (const c of centres) {
      // Skip those overlapping finder patterns.
      if ((r === 6 && c === 6) || (r === 6 && c === size - 7) || (r === size - 7 && c === 6)) continue;
      if (reserved[r][c]) continue;
      for (let dr = -2; dr <= 2; dr++) {
        for (let dc = -2; dc <= 2; dc++) {
          const isRing = Math.max(Math.abs(dr), Math.abs(dc)) !== 1;
          setFn(r + dr, c + dc, isRing, true);
        }
      }
    }
  }

  // Dark module.
  setFn(size - 8, 8, true, true);

  // Reserve format-info areas (filled later).
  for (let i = 0; i < 9; i++) {
    if (!reserved[8][i]) reserved[8][i] = true;
    if (!reserved[i][8]) reserved[i][8] = true;
  }
  for (let i = 0; i < 8; i++) {
    reserved[8][size - 1 - i] = true;
    reserved[size - 1 - i][8] = true;
  }

  // Place data bits in zig-zag, skipping reserved modules.
  const dataBits: number[] = [];
  for (const byte of bytes) for (let i = 7; i >= 0; i--) dataBits.push((byte >> i) & 1);

  let bitIdx = 0;
  let upward = true;
  for (let col = size - 1; col > 0; col -= 2) {
    if (col === 6) col--; // skip the vertical timing column
    for (let i = 0; i < size; i++) {
      const row = upward ? size - 1 - i : i;
      for (let c = 0; c < 2; c++) {
        const cc = col - c;
        if (reserved[row][cc]) continue;
        const bit = bitIdx < dataBits.length ? dataBits[bitIdx] : 0;
        matrix[row][cc] = bit === 1;
        bitIdx++;
      }
    }
    upward = !upward;
  }

  // Apply mask 0 ((row + col) % 2 === 0) to data modules only.
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (reserved[r][c]) continue;
      if ((r + c) % 2 === 0) matrix[r][c] = !matrix[r][c];
    }
  }

  // Format information for EC level M + mask 0.
  const formatData = (FORMAT_EC_BITS_M << 3) | 0; // mask pattern 0
  let formatBits = formatData << 10;
  const formatGen = 0b10100110111;
  let rem = formatBits;
  for (let i = 14; i >= 10; i--) {
    if ((rem >> i) & 1) rem ^= formatGen << (i - 10);
  }
  formatBits = ((formatData << 10) | rem) ^ 0b101010000010010;

  const fmt: number[] = [];
  for (let i = 14; i >= 0; i--) fmt.push((formatBits >> i) & 1);

  // Place format bits (two copies).
  const place = (r: number, c: number, bit: number) => {
    matrix[r][c] = bit === 1;
  };
  // Around top-left finder.
  for (let i = 0; i <= 5; i++) place(8, i, fmt[i]);
  place(8, 7, fmt[6]);
  place(8, 8, fmt[7]);
  place(7, 8, fmt[8]);
  for (let i = 9; i <= 14; i++) place(14 - i, 8, fmt[i]);
  // Around the other two finders.
  for (let i = 0; i <= 7; i++) place(size - 1 - i, 8, fmt[i]);
  for (let i = 8; i <= 14; i++) place(8, size - 15 + i, fmt[i]);

  return matrix.map((row) => row.map((v) => v === true));
}

export interface QrCodeProps {
  /** The value to encode (e.g. the site URL). */
  value: string;
  /** Pixel size of the rendered square. Defaults to 160. */
  size?: number;
  /** Accessible label for the QR image. */
  label: string;
  /** Foreground (module) colour. Defaults to a dark brand violet. */
  fgColor?: string;
  /** Background colour. Defaults to white. */
  bgColor?: string;
  /** Quiet-zone width in modules. QR spec recommends 4. */
  margin?: number;
  className?: string;
}

/**
 * Renders an accessible, self-contained QR code as an inline SVG.
 * Returns null if the value can't be encoded (defensive — should not happen
 * for normal URLs).
 */
export function QrCode({
  value,
  size = 160,
  label,
  fgColor = '#3b0764', // violet-950 — high contrast on white
  bgColor = '#ffffff',
  margin = 4,
  className,
}: QrCodeProps) {
  let matrix: boolean[][];
  try {
    const { spec, bytes } = encodeByteMode(value);
    matrix = buildMatrix(spec, bytes);
  } catch {
    return null;
  }

  const count = matrix.length;
  const total = count + margin * 2;

  // Build a single path string for all dark modules (compact + fast to render).
  let path = '';
  for (let r = 0; r < count; r++) {
    for (let c = 0; c < count; c++) {
      if (matrix[r][c]) {
        path += `M${c + margin} ${r + margin}h1v1h-1z`;
      }
    }
  }

  return (
    <svg
      role="img"
      aria-label={label}
      width={size}
      height={size}
      viewBox={`0 0 ${total} ${total}`}
      xmlns="http://www.w3.org/2000/svg"
      shapeRendering="crispEdges"
      className={className}
    >
      <rect width={total} height={total} fill={bgColor} />
      <path d={path} fill={fgColor} />
    </svg>
  );
}

export default QrCode;
